import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AuthUserType } from '@crema/models/AuthUser';
import jwtAxios, { setAuthToken } from './index';
import { useInfoViewActionsContext } from '@crema/context/InfoViewContextProvider';
import { useNavigate } from 'react-router-dom';
import iamAxios, { setIAMAuthToken } from '../iam-auth';

interface JWTAuthContextProps {
  user: AuthUserType | null | undefined;
  originalUser?: AuthUserType | null | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface SignUpProps {
  name: string;
  email: string;
  password: string;
  roles: string[];
  houseNumber: string;
  streetName: string;
  city: string;
  stateCode: string;
  zip: string;
  country: string;
}

interface SignInProps {
  email: string;
  password: string;
}

interface JWTAuthActionsProps {
  signUpUser: (data: SignUpProps) => void;
  signInUser: (data: SignInProps) => void;
  logout: () => void;
  setAuthData: (data: JWTAuthContextProps) => void;
}

const JWTAuthContext = createContext<JWTAuthContextProps>({
  user: null,
  originalUser: null,
  isAuthenticated: false,
  isLoading: true,
});
const JWTAuthActionsContext = createContext<JWTAuthActionsProps>({
  signUpUser: () => { },
  signInUser: () => { },
  logout: () => { },
  setAuthData: () => { },
});

export const useJWTAuth = () => useContext(JWTAuthContext);

export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

interface JWTAuthAuthProviderProps {
  children: ReactNode;
}

const JWTAuthAuthProvider: React.FC<JWTAuthAuthProviderProps> = ({
  children,
}) => {
  const [firebaseData, setJWTAuthData] = useState<JWTAuthContextProps>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const infoViewActionsContext = useInfoViewActionsContext();

  const navigate = useNavigate();

  useEffect(() => {
    const getAuthUser = async () => {
      console.log('getAuthUser');
      try {
        const token = localStorage.getItem('token');
        const email = localStorage.getItem('email');
        const password = localStorage.getItem('password');

        if (!token || !email || !password) {
          setJWTAuthData({
            user: undefined,
            isLoading: false,
            isAuthenticated: false,
          });
          return;
        }
        const { data: loginResponse } = await iamAxios.post('/auth/login', {
          username: email,
          password,
        });
        console.log('res', loginResponse);
        localStorage.setItem('token', loginResponse.accessToken);
        setAuthToken(loginResponse.accessToken);
        setIAMAuthToken(loginResponse.accessToken);
        const roles = loginResponse.roles;
        const tenantId = loginResponse.tenantId;

        if (roles.includes('CSP_BUSINESS') || roles.includes('CSP_TECHNICAL')
          || roles.includes('CSP_ADMIN') || roles.includes('CSP_READ_ONLY')) {
          const { data: cspUserDetails } = await jwtAxios.post('/csp/getcsp', {
            email: tenantId,
          });

          if (roles.includes('CSP_BUSINESS')) {
            setJWTAuthData({
              user: { ...cspUserDetails, roles },
              isAuthenticated: true,
              isLoading: false,
            });
          } else if (roles.includes('CSP_ADMIN')){
            setJWTAuthData({
              user: { ...cspUserDetails, email, name: 'CSP_ADMIN', roles },
              isAuthenticated: true,
              isLoading: false,
            });
          } else if (roles.includes('CSP_READ_ONLY')){
            setJWTAuthData({
              user: { ...cspUserDetails, email, name: 'CSP_READ_ONLY', roles },
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            setJWTAuthData({
              user: { ...cspUserDetails, email, name: 'CSP User', roles },
              isAuthenticated: true,
              isLoading: false,
            });
          }
        } else if (roles.includes('ENTERPRISE_ADMIN')) {
          const { data: enterpriseDetails } = await jwtAxios.get(`/enterprise/getenterpriseByEmail/${email}`);

          setJWTAuthData({
            user: { email, name: enterpriseDetails.contact || roles[0], roles, enterpriseId: enterpriseDetails.enterpriseId },
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setJWTAuthData({
            user: { email, name: roles[0], roles },
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } catch (error) {
        setJWTAuthData({
          ...firebaseData,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    getAuthUser();
  }, []);

  const signInUser = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    infoViewActionsContext.fetchStart();
    // let roles: string[] = [];
    // let tenantId: string = '';
    try {
      const { data: loginResponse } = await iamAxios.post('/auth/login', {
        username: email,
        password,
      });
      // if (password === 'Temp123$') {
      //   navigate('/update-password');
      //   infoViewActionsContext.fetchSuccess();
      //   return;
      // } else {
      localStorage.setItem('token', loginResponse.accessToken);
      localStorage.setItem('email', email);
      localStorage.setItem('password', password);
      setAuthToken(loginResponse.accessToken);
      setIAMAuthToken(loginResponse.accessToken);
      const roles = loginResponse.roles;
      const tenantId = loginResponse.tenantId;

      if (roles.includes('CSP_BUSINESS') || roles.includes('CSP_TECHNICAL')
        || roles.includes('CSP_ADMIN') || roles.includes('CSP_READ_ONLY')) {
        const { data: cspUserDetails } = await jwtAxios.post('/csp/getcsp', {
          email: tenantId,
        });

        if (roles.includes('CSP_BUSINESS')) {
          setJWTAuthData({
            user: { ...cspUserDetails, roles },
            isAuthenticated: true,
            isLoading: false,
          });
        } else if (roles.includes('CSP_ADMIN')){
          setJWTAuthData({
            user: { ...cspUserDetails, email, name: 'CSP_ADMIN', roles },
            isAuthenticated: true,
            isLoading: false,
          });
        } else if (roles.includes('CSP_READ_ONLY')){
          setJWTAuthData({
            user: { ...cspUserDetails, email, name: 'CSP_READ_ONLY', roles },
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setJWTAuthData({
            user: { ...cspUserDetails, email, name: 'CSP User', roles },
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } else if (roles.includes('ENTERPRISE_ADMIN')) {
        console.log('roles : ',roles);
        const { data: enterpriseDetails } = await jwtAxios.get(`/enterprise/getenterpriseByEmail/${email}`);

        setJWTAuthData({
          user: { email, name: enterpriseDetails.contact || roles[0], roles, enterpriseId: enterpriseDetails.enterpriseId },
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        setJWTAuthData({
          user: { email, name: roles[0], roles },
          isAuthenticated: true,
          isLoading: false,
        });
      }
      infoViewActionsContext.fetchSuccess();
      // }
    } catch (error: any) {
      console.log('error', error);
      setJWTAuthData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      infoViewActionsContext.fetchError(
        'Incorrect password. Please try again or contact an administrator via support@sabrhub.com.'
      );
      return;
    }

  };

  const signUpUser = async ({
    name,
    email,
    password,
    roles,
    houseNumber = '',
    streetName = '',
    city = '',
    stateCode = '',
    zip = '',
    country = '',
  }: {
    name: string;
    email: string;
    password: string;
    roles: string[];
    houseNumber: string;
    streetName: string;
    city: string;
    stateCode: string;
    zip: string;
    country: string;
  }) => {
    infoViewActionsContext.fetchStart();
    try {
      const signUpResponse = await iamAxios.post('/auth/createuser', {
        email,
        password,
        roles,
        services: ['Webex'],
        // tenantId: email,
      });
      const { data } = await jwtAxios.post('/csp', {
        email,
        name,
        carrier: 'BANDWIDTH',
        houseNumber,
        streetName,
        city,
        stateCode,
        zip,
        country,
        locationId: '',
        mmsLocationId: '',
        subAccountId: '',
        authorizingPersonName: name,
        allowMasterToken: false,
        mmsFeature: '',
      });
      console.log('data', data);
      // navigate('/signin');
      // localStorage.setItem('token', data.token);
      // setAuthToken(data.token);
      // const res = await jwtAxios.get('/auth');
      // setJWTAuthData({
      //   user: res.data,
      //   isAuthenticated: true,
      //   isLoading: false,
      // });
      infoViewActionsContext.fetchSuccess();
      // return true;
    } catch (error) {
      // setJWTAuthData({
      //   ...firebaseData,
      //   isAuthenticated: false,
      //   isLoading: false,
      // });
      infoViewActionsContext.fetchError('Something went wrong');
      // return false;
    }
  };

  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    localStorage.removeItem('password');
    setAuthToken();
    setJWTAuthData({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
  };

  const setAuthData = (data: JWTAuthContextProps) => {
    setJWTAuthData({ ...data });
  };

  return (
    <JWTAuthContext.Provider
      value={{
        ...firebaseData,
      }}
    >
      <JWTAuthActionsContext.Provider
        value={{
          signUpUser,
          signInUser,
          logout,
          setAuthData,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};
export default JWTAuthAuthProvider;
